import { useMemo } from "react";
import { useRouteContext, getRouteApi } from "@tanstack/react-router";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  useCertifications,
  useCertificationStats,
  useCertificationLogs,
  useMonthlyCertificationReports,
} from "@/queries";
import { fetchAuthenticatedData } from "@/queries/shared";
import { parseCertificationMonth } from "@/utils/certifications";
import { endOfLocalDay } from "@/utils/dates";
import { isSupplementalReport } from "@/utils/reportFilters";
import { Certification, CertificationLog } from "@/models/file-submission";
import { postCertificationEdit } from "@/components/certifications/Revisions/hooks/useRevisions";
import type { CertifierFormData } from "@/components/certifications/Submissions/useSubmission";
import { useDiscrepanciesPage } from "@/components/discrepancies/data-discrepancies/hooks/useDiscrepanciesPage";
import { useRecordPermissions } from "@/components/discrepancies/data-discrepancies/hooks/useRecordPermissions";

const rootRoute = getRouteApi("__root__");

export default function useCertificationPage(
  leaid: string,
  certificationid: string
) {
  const leaContext = useRouteContext({ from: "/$leaid" });
  const esiidPath = leaContext.currentUserOrg.esiIdPath;
  const globalState = rootRoute.useLoaderData();
  const queryClient = useQueryClient();

  // ── Data ───────────────────────────────────────────────────

  const { data: certifications } = useCertifications(esiidPath);
  const certification = certifications?.find(
    (c) => String(c.ReportingCalendarId) === certificationid
  );

  const esiid = certification?.Esiid ? String(certification.Esiid) : undefined;
  const certId = certification?.Id ? String(certification.Id) : undefined;
  const anomalyRunId = certification?.AnomalyRunId
    ? String(certification.AnomalyRunId)
    : certification?.AnomalyRun?.Id
      ? String(certification.AnomalyRun.Id)
      : undefined;

  const certStats = useCertificationStats(esiidPath, certId ?? "");
  const { data: certLogs, isPending: certLogsIsPending } = useCertificationLogs(
    esiidPath,
    certId ?? ""
  );
  const lockedAnomalyRunId: string | null = anomalyRunId ?? null;
  const hasLockedRevision = !!lockedAnomalyRunId;

  const { data: rawReportItems = [] } = useMonthlyCertificationReports(leaid);
  const reportItems = rawReportItems.filter(
    (item) => !isSupplementalReport(item)
  );

  const reportIdToIndex = new Map<number, number>();
  reportItems.forEach((item, i) => reportIdToIndex.set(item.reportId, i + 1));

  const discrepancies = useDiscrepanciesPage(certificationid);
  const { canEditRecord } = useRecordPermissions();
  const canCertify = leaContext.hasRole("Certifier") ?? false;
  const certificationMonth = parseCertificationMonth(
    certificationid,
    certification?.WindowStart
  );
  const rawStatus = certification?.Status ?? "—";
  const certificationStatus = rawStatus === "Open" ? "Uncertified" : rawStatus;

  const isCycleEnded = (() => {
    if (!certification?.AmendmentEnd) return false;
    return new Date() > endOfLocalDay(certification.AmendmentEnd);
  })();

  let totalErrors = 0;
  let totalWarnings = 0;
  let mostRecentErrors = 0;
  let mostRecentRevisionId: string | null = null;
  let mostRecentReportingHistoryId: string | null = null;
  let lockedReportingHistoryId: string | null = null;

  if (
    certStats.data &&
    Array.isArray(certStats.data) &&
    certStats.data.length > 0
  ) {
    const sorted = [...certStats.data].sort(
      (a, b) => (b.AnomalyRunId ?? 0) - (a.AnomalyRunId ?? 0)
    );
    mostRecentErrors = sorted[0].Errors ?? 0;
    mostRecentRevisionId = String(sorted[0].AnomalyRunId);
    mostRecentReportingHistoryId =
      sorted[0].AnomalyRun?.ReportingHistoryId != null
        ? String(sorted[0].AnomalyRun.ReportingHistoryId)
        : null;
    const lockedStat = lockedAnomalyRunId
      ? sorted.find((s) => String(s.AnomalyRunId) === lockedAnomalyRunId)
      : null;
    const target = lockedStat ?? sorted[0];
    totalErrors = target.Errors ?? 0;
    totalWarnings = target.Warnings ?? 0;
    lockedReportingHistoryId =
      lockedStat?.AnomalyRun?.ReportingHistoryId != null
        ? String(lockedStat.AnomalyRun.ReportingHistoryId)
        : null;
  }

  const canReviewReports =
    canCertify && (hasLockedRevision || mostRecentErrors === 0);

  const persistedReadIds = useMemo<Set<string>>(() => {
    if (!certLogs || !lockedAnomalyRunId) return new Set();

    const sorted = [...certLogs].sort((a, b) => a.Id - b.Id);

    const lastResetId = sorted
      .filter(
        (log) =>
          String(log.UnLockAnomalyRunId) === lockedAnomalyRunId ||
          (log.StatusUpdate != null &&
            log.StatusUpdate !== "Certified" &&
            log.ReviewedReportId == null &&
            (String(log.LockAnomalyRunId) === lockedAnomalyRunId ||
              String(log.UnLockAnomalyRunId) === lockedAnomalyRunId))
      )
      .reduce((max, log) => (log.Id > max ? log.Id : max), 0);

    const ids = new Set<string>();
    sorted.forEach((log) => {
      if (log.ReviewedReportId == null) return;
      if (log.Id <= lastResetId) return;
      if (String(log.LockAnomalyRunId) === lockedAnomalyRunId) {
        ids.add(String(log.ReviewedReportId));
      }
    });

    return ids;
  }, [certLogs, lockedAnomalyRunId]);

  const getReviewerName = (): string => {
    const accounts = globalState.pca.getAllAccounts();
    if (accounts.length === 0) return "";
    return accounts[0].name || accounts[0].username || "";
  };

  const optimisticStatusUpdate = (status: string, reviewer: string) => {
    const now = new Date().toISOString();
    queryClient.setQueryData(
      ["certifications", esiidPath],
      (old: Certification[] | undefined) =>
        old?.map((c) =>
          String(c.Id) === certId
            ? {
                ...c,
                Status: status,
                LastReviewer: reviewer,
                LastReviewerDate: now,
              }
            : c
        )
    );
  };

  const postCertificationLog = async (payload: object): Promise<boolean> => {
    try {
      await fetchAuthenticatedData<CertificationLog>(
        globalState,
        `/json/certificationlogs/${esiidPath}/add`,
        {
          method: "POST",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
        }
      );
      queryClient.invalidateQueries({
        queryKey: ["certification_logs", esiidPath, certId],
      });
      return true;
    } catch (err) {
      console.error(
        "[useCertificationPage] Failed to post certification log:",
        err
      );
      return false;
    }
  };

  const invalidateCertifications = () => {
    queryClient.invalidateQueries({ queryKey: ["certifications", esiidPath] });
    queryClient.invalidateQueries({
      queryKey: ["certification_stats", esiidPath, certId],
    });
    queryClient.invalidateQueries({
      queryKey: ["certification_logs", esiidPath, certId],
    });
  };

  // ── Auto-lock ──────────────────────────────────────────────

  const autoLockMutation = useMutation({
    mutationFn: async ({
      revisionId,
      onSuccess,
      onError,
    }: {
      revisionId: string;
      onSuccess?: (revisionId: string) => void;
      onError?: () => void;
    }) => {
      const cert = await fetchAuthenticatedData<Certification>(
        globalState,
        `/json/certification/${esiidPath}/${certId}`
      );
      cert.AnomalyRunId = parseInt(revisionId, 10);
      cert.LastReviewer = getReviewerName();
      await postCertificationEdit(globalState, esiidPath, cert);
      return { revisionId, onSuccess, onError };
    },
    onSuccess: ({ revisionId, onSuccess }) => {
      queryClient.invalidateQueries({
        queryKey: ["certifications", esiidPath],
      });
      queryClient.invalidateQueries({
        queryKey: ["certification_stats", esiidPath, certId],
      });

      postCertificationLog({
        CertificationId: Number(certId),
        LockAnomalyRunId: Number(revisionId),
      }).then(() => {
        onSuccess?.(revisionId);
      });
    },
    onError: (error, { onError }) => {
      console.error("[useCertificationPage] Auto-lock failed:", error);

      onError?.();
    },
  });

  const handleAutoLock = (
    revisionId: string,
    onLockSuccess?: (revisionId: string) => void,
    onLockError?: () => void
  ) => {
    if (!autoLockMutation.isPending) {
      autoLockMutation.mutate({
        revisionId,
        onSuccess: onLockSuccess,
        onError: onLockError,
      });
    }
  };

  // ── Report viewed ──────────────────────────────────────────

  const handleReportViewed = async (
    reportId: string,
    overrideLockId?: string
  ): Promise<boolean> => {
    if (!certId || certificationStatus === "Certified") return false;
    const parsedReportId = parseInt(reportId, 10);
    if (Number.isNaN(parsedReportId)) {
      console.error("[useCertificationPage] Invalid reportId:", reportId);
      return false;
    }

    const effectiveLockId = overrideLockId ?? lockedAnomalyRunId;
    if (!effectiveLockId) {
      console.warn(
        "[useCertificationPage] Skipping report view log: no locked revision."
      );
      return false;
    }

    return postCertificationLog({
      CertificationId: Number(certId),
      ReviewedReportId: parsedReportId,
      LockAnomalyRunId: Number(effectiveLockId),
      Reviewer: getReviewerName(),
      ReviewerDate: new Date().toISOString(),
    });
  };

  // ── Certify ────────────────────────────────────────────────

  const handleCertify = async (data: CertifierFormData): Promise<boolean> => {
    if (!certId) return false;
    try {
      const cert = await fetchAuthenticatedData<Certification>(
        globalState,
        `/json/certification/${esiidPath}/${certId}`
      );

      cert.Status = "Certified";
      cert.CertifierFirstName = data.firstName;
      cert.CertifierLastName = data.lastName;
      cert.CertifierEmail = data.email;
      cert.CertifierPhoneNumber = data.phoneNumber;
      cert.CertifierExtension = data.extension || null;

      const reviewerName =
        getReviewerName() || `${data.firstName} ${data.lastName}`;
      cert.LastReviewer = reviewerName;

      await postCertificationEdit(globalState, esiidPath, cert);
      optimisticStatusUpdate("Certified", reviewerName);

      await postCertificationLog({
        CertificationId: Number(certId),
        StatusUpdate: "Certified",
        LockAnomalyRunId: lockedAnomalyRunId
          ? Number(lockedAnomalyRunId)
          : null,
        Reviewer: reviewerName,
        ReviewerDate: new Date().toISOString(),
      });

      invalidateCertifications();
      return true;
    } catch (err) {
      console.error("[useCertificationPage] Certification failed:", err);
      return false;
    }
  };

  // ── Decertify ──────────────────────────────────────────────

  const handleDecertify = async (): Promise<boolean> => {
    if (!certId) return false;
    try {
      const cert = await fetchAuthenticatedData<Certification>(
        globalState,
        `/json/certification/${esiidPath}/${certId}`
      );

      const unlockedRunId = cert.AnomalyRunId
        ? String(cert.AnomalyRunId)
        : cert.AnomalyRun?.Id
          ? String(cert.AnomalyRun.Id)
          : null;

      cert.Status = "Decertified";
      cert.CertifierFirstName = null;
      cert.CertifierLastName = null;
      cert.CertifierEmail = null;
      cert.CertifierPhoneNumber = null;
      cert.CertifierExtension = null;
      cert.AnomalyRunId = null;

      const reviewerName = getReviewerName();
      cert.LastReviewer = reviewerName;

      await postCertificationEdit(globalState, esiidPath, cert);
      optimisticStatusUpdate("Decertified", reviewerName);

      await postCertificationLog({
        CertificationId: Number(certId),
        StatusUpdate: "Decertified",
        LockAnomalyRunId: unlockedRunId ? Number(unlockedRunId) : null,
        Reviewer: reviewerName,
        ReviewerDate: new Date().toISOString(),
      });

      await postCertificationLog({
        CertificationId: Number(certId),
        UnLockAnomalyRunId: unlockedRunId ? Number(unlockedRunId) : null,
        Reviewer: reviewerName,
        ReviewerDate: new Date().toISOString(),
      });

      invalidateCertifications();
      return true;
    } catch (err) {
      console.error("[useCertificationPage] Decertification failed:", err);
      return false;
    }
  };

  // ── Return ─────────────────────────────────────────────────

  return {
    esiidPath,
    leaContext,
    canCertify,
    canEditRecord,
    certifications,
    certification,
    certId,
    esiid,
    certStats,
    certificationMonth,
    certificationStatus,
    lockedAnomalyRunId,
    hasLockedRevision,
    mostRecentRevisionId,
    mostRecentReportingHistoryId,
    lockedReportingHistoryId,
    totalErrors,
    totalWarnings,
    canReviewReports,
    persistedReadIds,
    certLogsIsPending,
    isCycleEnded,
    discrepancies,
    reportItems,
    reportIdToIndex,
    handleAutoLock,
    handleReportViewed,
    handleCertify,
    handleDecertify,
  };
}
